import { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import {
  ClockIcon,
  PrinterIcon,
  Separator,
  ShopIcon,
  WalletIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { formatSum } from 'utils';

import s from './MoneyByType.module.scss';
import {
  IGetMoneyByTypeResponse,
  IGetMoneyByTypeRequest,
  GET_MONEY_BY_TYPE,
} from './schema';

interface IWorkerBlockProps {
  workShiftId?: number;
  small?: boolean;
}
export const MoneyByType: FC<IWorkerBlockProps> = ({ workShiftId, small }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data: workshiftMoneyData } = useQuery<
    IGetMoneyByTypeResponse,
    IGetMoneyByTypeRequest
  >(GET_MONEY_BY_TYPE, {
    fetchPolicy: 'cache-only',
    variables: { id: workShiftId },
    skip: !permissions?.has('work_shift_active'),
  });

  const {
    good = 0,
    deposit = 0,
    service = 0,
    tariff = 0,
  } = workshiftMoneyData?.getWorkShiftPaymentOverviewData?.sum ?? {};

  return (
    <div className={cn(s.container, small && s.small)}>
      <div className={s.column}>
        <div className={s.block}>
          <span className={s.title}>
            <ClockIcon size="m" color="primary" />
            {t('category.tariffs')}
          </span>
          <span>
            {formatSum(tariff, currency.sign, { maximumFractionDigits: 2 })}
          </span>
        </div>
        <Separator className={s.separator} />
        <div className={s.block}>
          <span className={s.title}>
            <WalletIcon size="m" color="primary" />
            {t('category.deposits')}
          </span>
          <span>
            {formatSum(deposit, currency.sign, { maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
      <div className={s.column}>
        <div className={s.block}>
          <span className={s.title}>
            <ShopIcon size="m" color="primary" />
            {t('category.goods')}
          </span>
          <span>
            {formatSum(good, currency.sign, {
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <Separator className={s.separator} />
        <div className={s.block}>
          <span className={s.title}>
            <PrinterIcon size="m" color="primary" />
            {t('category.services')}
          </span>
          <span>
            {formatSum(service, currency.sign, {
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>
    </div>
  );
};
